<script setup>
// §8.1 UserFooter — avatar + name + in-prose support link (underlined, D6).
//
// When `menuItems` are supplied the whole identity row becomes the trigger for a
// menu that opens UPWARDS (the footer sits at the bottom of the sidebar), which
// is where Profile / What Lotus captures / Sign out live. With no items it stays
// the plain identity block it has always been.
import { computed } from 'vue'
import LProseLink from '../actions/ProseLink.vue'
import LDropdownMenu from '../actions/DropdownMenu.vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	name: { type: String, required: true },
	initial: { type: String, default: null },

	// The line under the name — the firm, and the person's role in it.
	subtitle: { type: String, default: null },

	// LDropdownMenu items: [{ label, icon?, destructive?, onSelect } | { separator: true }]
	menuItems: { type: Array, default: () => [] },

	// Where the name links when there is no menu.
	href: { type: String, default: '/my-profile' },
})

const hasMenu = computed(() => props.menuItems.length > 0)
const avatarInitial = computed(() => props.initial ?? props.name.charAt(0))
</script>

<template>
	<div class="foot">

		<LDropdownMenu v-if="hasMenu" :items="menuItems" align="start" placement="top" class="menu-wrap">
			<template #trigger="{ toggle, open, onkeydown }">
				<button type="button" class="who is-trigger" :aria-expanded="open" aria-haspopup="menu"
					@click="toggle" @keydown="onkeydown">
					<div class="av" aria-hidden="true">
						<span class="initial">{{ avatarInitial }}</span>
					</div>
					<div class="who-text">
						<div class="name">{{ name }}</div>
						<div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
					</div>
					<LIcon class="chev" :name="open ? 'chevron-down' : 'chevron-up'" :size="11" />
				</button>
			</template>
		</LDropdownMenu>

		<div v-else class="who">
			<div class="av" aria-hidden="true">
				<span class="initial">{{ avatarInitial }}</span>
			</div>
			<div class="who-text">
				<div class="name"><a :href="href" style="color: inherit">{{ name }}</a></div>
				<div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
			</div>
		</div>

		<div class="help prose">Need help? <LProseLink href="mailto:support@lotusai.co">Contact support</LProseLink>
		</div>
	</div>
</template>

<style scoped>
.foot
{
	border-top: 1px solid var(--line-soft);
	padding: var(--sp-4) var(--sp-5);
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--sp-2);
	margin-top: auto;
}

/* The menu is positioned against the trigger, so the wrapper has to span the
   footer rather than shrink-wrap the button. */
.menu-wrap
{
	display: block;
	width: 100%;
}

/* LDropdownMenu wraps the trigger slot in a span for focus return; it has to
   fill the footer so the identity row is the whole hit area. */
.menu-wrap :deep(> span)
{
	display: block;
}

.who
{
	display: flex;
	align-items: center;
	gap: 8px;
}

.who.is-trigger
{
	width: 100%;
	background: none;
	border: 0;
	padding: var(--sp-2);
	border-radius: var(--r);
	text-align: left;
	cursor: pointer;
	color: inherit;
	font-family: inherit;
	transition: background var(--t-fast) var(--ease);
}

.who.is-trigger:hover
{
	background: var(--surface-sunken);
}

.av
{
	width: 34px;
	height: 34px;
	border-radius: 50%;
	background: var(--brand);
	color: var(--brand-on);
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: var(--font-display);
	flex: none;
}

.initial
{
	/* It's a bit pushed up otherwise */
	padding-top: 3px;
}

.who-text
{
	min-width: 0;
}

.name
{
	font-weight: 700;
	font-size: 1.2em;
}

.subtitle
{
	font-size: var(--text-chip);
	color: var(--text-3);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.chev
{
	margin-left: auto;
	color: var(--text-3);
	flex: none;
}

.help
{
	font-size: 12px;
	color: var(--text-3);
}
</style>
